import { rolesForPath } from "../config/navConfig";
import { pathAllowedByRights } from "../config/accessRights";

/**
 * Главный экран (планшет руководителя, правки №41–42): каталог окон и чистые функции раскладки. [Claude] 2026-11-03, правка №41.
 * Раскладка хранится на сервере (ui-preferences «executive_board»): порядок окон, вкл/выкл, ширина, настройки окна.
 */

export const PERIODS = [
    { value: 7, label: "7 дней" },
    { value: 14, label: "14 дней" },
    { value: 30, label: "30 дней" },
    { value: 60, label: "60 дней" },
    { value: 90, label: "Квартал" },
    { value: 180, label: "Полгода" },
    { value: 365, label: "Год" },
];

export const SPANS = [
    { value: 1, label: "¼" },
    { value: 2, label: "½" },
    { value: 3, label: "¾" },
    { value: 4, label: "Вся ширина" },
];

export const KPI_METRICS = [
    { key: "open_vacancies", label: "Открытых вакансий", block: "pipeline" },
    { key: "active_candidates", label: "Кандидатов в работе", block: "pipeline" },
    { key: "applied", label: "Откликов за период", block: "pipeline" },
    { key: "hired", label: "Вышли на работу", block: "staff" },
    { key: "lost", label: "Отсеяно / отказались", block: "pipeline" },
    { key: "time_to_hire_median", label: "Срок найма, дн. (медиана)", block: "staff" },
    { key: "interviews_week", label: "Собесед. и тест-дней на 7 дн.", block: "pipeline" },
    { key: "interviews_today", label: "Собеседований сегодня", block: "pipeline" },
    { key: "requests_waiting", label: "Заявок ждут решения", block: "requests" },
    { key: "adaptation_active", label: "На адаптации", block: "adaptation" },
    { key: "adaptation_overdue", label: "Просрочено срезов", block: "adaptation" },
    { key: "headcount", label: "Сотрудников в штате", block: "staff" },
];

export const QUEUE_ITEMS = [
    ["new", "Разобрать новые отклики"],
    ["test_waiting", "Тест отправлен — нет результата"],
    ["test_passed", "Тест пройден — пригласить на собеседование"],
    ["interview_soon", "Собеседования сегодня и завтра"],
    ["interview_result", "Собеседование прошло — внести итог"],
    ["decision", "Ждут решения после собеседования"],
    ["no_show", "Не пришли — перезвонить или закрыть"],
    ["offer", "Оффер отправлен — ждём ответа"],
    ["onboarding", "Оффер принят — оформление"],
];

const ALL_KPI = KPI_METRICS.map((m) => m.key);
const DEFAULT_KPI = ["open_vacancies", "active_candidates", "applied", "hired", "time_to_hire_median", "interviews_week", "requests_waiting", "adaptation_overdue"];

/**
 * options — описание настроек окна: type select | multi | bool; values — варианты.
 */
export const WIDGETS = [
    {
        id: "alerts", group: "Общее", path: "/dashboard", blocks: [], title: "Требует внимания", span: 4,
        help: "Что горит прямо сейчас: просрочки, вакансии без кандидатов, заявки без решения, срезы адаптации.",
        options: [{ key: "limit", label: "Сколько показывать", type: "select", values: [[4, "4"], [8, "8"], [20, "Все"]], default: 8 }],
    },
    {
        id: "queue", group: "Кандидаты", path: "/candidates", blocks: ["pipeline"], title: "Кандидаты: ждут действия", span: 4,
        help: "Очередь рекрутёра по всем открытым вакансиям: сколько кандидатов ждёт действия на каждом шаге, сколько уже просрочено (новый отклик — больше 1 дня, тест без результата — больше 2, тест пройден — больше 1, решение после собеседования — больше 2, неявка — больше 1, ответ на оффер — больше 2, оформление — больше 3 дней) и на каких вакансиях больше всего.",
        options: [
            { key: "items", label: "Какие шаги показывать", type: "multi", values: QUEUE_ITEMS, default: QUEUE_ITEMS.map(([k]) => k) },
            { key: "hide_empty", label: "Скрывать пустые шаги", type: "bool", default: false },
            { key: "vacancies", label: "Показывать вакансии", type: "bool", default: true },
        ],
    },
    {
        id: "kpi", group: "Общее", path: "/dashboard", blocks: [], title: "Ключевые показатели", span: 4,
        help: "Главные цифры за выбранный период и по выбранному отделу.",
        options: [{ key: "metrics", label: "Какие показатели", type: "multi", values: KPI_METRICS.map((m) => [m.key, m.label]), default: DEFAULT_KPI }],
    },
    {
        id: "vacancies", group: "Вакансии", path: "/vacancies", blocks: ["pipeline"], title: "Вакансии: жизненный цикл", span: 4,
        help: "Каждая открытая вакансия: сколько дней открыта, сколько кандидатов на каждом этапе, когда было последнее движение и светофор.",
        options: [
            { key: "department", label: "Отдел", type: "department", default: "" },
            { key: "show", label: "Показывать", type: "select", values: [["all", "Все"], ["problem", "Только проблемные"], ["red", "Только красные"]], default: "all" },
            { key: "sort", label: "Сортировка", type: "select", values: [["severity", "По светофору"], ["days_open", "Дольше открыты"], ["active", "Больше кандидатов"], ["last_move", "Дольше без движения"]], default: "severity" },
            { key: "limit", label: "Строк", type: "select", values: [[10, "10"], [25, "25"], [100, "Все"]], default: 25 },
            { key: "stages", label: "Колонки этапов", type: "bool", default: true },
        ],
    },
    {
        id: "funnel", group: "Кандидаты", path: "/candidates", blocks: ["pipeline"], title: "Воронка найма за период", span: 2,
        help: "Отклики, пришедшие за период: сколько из них дошло до каждого этапа. Холодный поиск считается вместе с откликами.",
        options: [{ key: "mode", label: "Процент", type: "select", values: [["first", "От откликов"], ["prev", "От прошлого этапа"]], default: "first" }],
    },
    {
        id: "now", group: "Кандидаты", path: "/candidates", blocks: ["pipeline"], title: "Где сейчас кандидаты", span: 2,
        help: "Активные кандидаты по этапам на текущий момент (без архива).",
        options: [{ key: "hide_empty", label: "Скрывать пустые этапы", type: "bool", default: false }],
    },
    {
        id: "flow", group: "Кандидаты", path: "/candidates", blocks: ["pipeline","staff"], title: "Динамика по неделям", span: 2,
        help: "Отклики, выходы на работу и отсев по неделям (понедельник — начало недели).",
        options: [
            { key: "series", label: "Ряды", type: "multi", values: [["applied", "Отклики"], ["hired", "Вышли"], ["lost", "Отсев"]], default: ["applied", "hired", "lost"] },
            { key: "chart", label: "Вид", type: "select", values: [["bar", "Столбцы"], ["line", "Линии"]], default: "bar" },
        ],
    },
    {
        id: "interviews", group: "Кандидаты", path: "/calendar", blocks: ["pipeline"], title: "Собеседования и тест-дни", span: 2,
        help: "Назначенные собеседования и тест-дни на ближайшие 7 дней и неявки за период.",
        options: [],
    },
    {
        id: "requests", group: "Вакансии", path: "/requests", blocks: ["requests"], title: "Заявки на подбор", span: 2,
        help: "Открытые заявки по статусам; какие ждут решения дольше всего; утверждённые без вакансии.",
        options: [{ key: "limit", label: "Сколько ждущих показывать", type: "select", values: [[5, "5"], [10, "10"]], default: 5 }],
    },
    {
        id: "adaptation", group: "Адаптация", path: "/tests/adaptation", blocks: ["adaptation"], title: "Адаптация", span: 2,
        help: "Сотрудники на адаптации, риски, просроченные срезы и итоги завершённых адаптаций за период.",
        options: [{ key: "list", label: "Список просрочек", type: "bool", default: true }],
    },
    {
        id: "staff", group: "Персонал", path: "/employees", blocks: ["staff"], title: "Персонал и найм", span: 2,
        help: "Штат, вышли и уволились за период, ранний уход (до 90 дней работы), срок найма от отклика до выхода.",
        options: [],
    },
    {
        id: "tests", group: "Тесты", path: "/tests/results", blocks: ["tests"], title: "Тестирования", span: 2,
        help: "Отправки тестов за период по статусам, ошибки отправки, результаты и средний балл.",
        options: [],
    },
    {
        id: "lost", group: "Кандидаты", path: "/candidates", blocks: ["pipeline"], title: "Причины отсева", span: 2,
        help: "На каком этапе и по какой причине теряем кандидатов (за период).",
        options: [{ key: "limit", label: "Сколько причин", type: "select", values: [[5, "5"], [8, "8"]], default: 8 }],
    },
    {
        id: "recruiters", group: "Персонал", path: "/analytics", blocks: ["recruiters"], title: "Рекрутёры: выходы (ВНР)", span: 2,
        help: "Сколько выходов закрыл каждый рекрутёр за период и сколько из них уже ушли.",
        options: [],
    },
    {
        id: "tasks", group: "Общее", path: "/dashboard", blocks: [], title: "Задачи на сегодня", span: 4,
        help: "Текущие задачи и уведомления платформы (как раньше на главной).",
        options: [{ key: "groups", label: "Разделы", type: "multi", values: [["vacancies", "Вакансии"], ["candidates", "Кандидаты"], ["calendar", "Календарь"], ["other", "Система"]], default: ["vacancies", "candidates", "calendar", "other"] }],
    },
    {
        id: "metric", group: "Общее", path: "/dashboard", blocks: [], title: "Одна цифра", span: 1,
        help: "Один показатель крупно. Добавьте несколько таких модулей с разными показателями и соберите свою панель цифр.",
        options: [
            { key: "metric", label: "Показатель", type: "metric", default: "open_vacancies" },
            { key: "caption", label: "Своя подпись (необязательно)", type: "text_short", default: "" },
        ],
    },
    {
        id: "today", group: "Кандидаты", path: "/candidates", blocks: ["pipeline"], title: "Сегодня", span: 2,
        help: "Что произошло сегодня: новые отклики, кандидаты, сдвинувшиеся по воронке, отсев, выходы и собеседования на сегодня.",
        options: [],
    },
    {
        id: "vacancy_focus", group: "Вакансии", path: "/vacancies", blocks: ["pipeline"], title: "Вакансия под лупой", span: 2,
        help: "Одна выбранная вакансия целиком: сколько дней открыта, где сейчас кандидаты, воронка откликов за период, сигналы и срок. Можно добавить несколько — по одной на важную вакансию.",
        options: [{ key: "vacancy", label: "Вакансия", type: "vacancy", default: "" }],
    },
    {
        id: "departments", group: "Вакансии", path: "/vacancies", blocks: ["pipeline"], title: "Найм по отделам", span: 2,
        help: "По каждому отделу: открытые вакансии, проблемные, кандидаты в работе и выходы за период.",
        options: [{ key: "sort", label: "Сортировка", type: "select", values: [["active", "Больше кандидатов"], ["vacancies", "Больше вакансий"], ["problem", "Больше проблемных"], ["name", "По названию"]], default: "active" }],
    },
    {
        id: "conversion", group: "Кандидаты", path: "/candidates", blocks: ["pipeline"], title: "Конверсия по вакансиям", span: 4,
        help: "Отклики за период по каждой вакансии и какая доля дошла до теста, собеседования, оффера и выхода. Видно, где воронка «течёт».",
        options: [
            { key: "department", label: "Отдел", type: "department", default: "" },
            { key: "sort", label: "Сортировка", type: "select", values: [["applied", "Больше откликов"], ["interview", "Лучше доходят до собеседования"], ["worst", "Хуже доходят до собеседования"]], default: "applied" },
            { key: "min", label: "Не показывать вакансии, где откликов меньше", type: "select", values: [[0, "0"], [5, "5"], [20, "20"]], default: 5 },
        ],
    },
    {
        id: "links", group: "Общее", path: "/dashboard", blocks: [], title: "Быстрые переходы", span: 2,
        help: "Кнопки на разделы, в которые вы ходите чаще всего. Список — только из доступных вам вкладок.",
        options: [{ key: "paths", label: "Разделы", type: "links", default: ["/requests", "/vacancies", "/candidates", "/calendar"] }],
    },
    {
        id: "note", group: "Общее", path: "/dashboard", blocks: [], title: "Заметка", span: 1,
        help: "Свой текст на главной: план на неделю, напоминание, договорённости. Видите только вы.",
        options: [
            { key: "title", label: "Заголовок", type: "text_short", default: "" },
            { key: "text", label: "Текст", type: "text", default: "" },
        ],
    },
];

export const WIDGET_BY_ID = Object.fromEntries(WIDGETS.map((w) => [w.id, w]));

export const DEFAULT_PREFS = { period_days: 30, configured: false, department: null, stale_days: 7, widgets: [] };

/** Стартовый набор для того, кто ещё не собирал свою главную. Дальше каждый добавляет и убирает сам. */
export const STARTER = ["alerts", "kpi", "queue", "vacancies", "funnel", "flow", "adaptation", "tasks"];

export const MODULE_GROUPS = ["Общее", "Вакансии", "Кандидаты", "Персонал", "Адаптация", "Тесты"];

export function defaultOptions(def) {
    const out = {};
    (def.options || []).forEach((o) => { out[o.key] = Array.isArray(o.default) ? [...o.default] : o.default; });
    return out;
}

export function newModule(type, id = type) {
    const def = WIDGET_BY_ID[type];
    return { id, type, enabled: true, span: def.span, options: defaultOptions(def) };
}

/**
 * Раскладка пользователя → список модулей на главной.
 * Модуль = экземпляр вида (type) со своими настройками; один вид можно добавить несколько раз.
 * Кто ещё не собирал главную — получает стартовый набор. Выключенные модули прежних версий убираются.
 */
export function mergeLayout(saved, configured = true) {
    const list = Array.isArray(saved) ? saved : [];
    if (!list.length && !configured) return STARTER.map((t) => newModule(t));
    const seen = new Set();
    const out = [];
    list.forEach((w) => {
        if (!w) return;
        const type = String(w.type || w.id || "");
        const def = WIDGET_BY_ID[type];
        const id = String(w.id || type);
        if (!def || seen.has(id) || w.enabled === false) return;
        seen.add(id);
        const span = Number(w.span);
        out.push({
            id,
            type,
            enabled: true,
            span: span >= 1 && span <= 4 ? span : def.span,
            options: { ...defaultOptions(def), ...(w.options || {}) },
        });
    });
    return out;
}

/** Добавить модуль (наверх — чтобы сразу было видно; дальше переставляется мышкой). */
export function addModule(layout, type) {
    if (!WIDGET_BY_ID[type]) return layout;
    const used = new Set(layout.map((w) => w.id));
    let id = type;
    for (let n = 2; used.has(id); n += 1) id = `${type}_${n}`;
    return [newModule(type, id), ...layout];
}

export function removeModule(layout, id) {
    return layout.filter((w) => w.id !== id);
}

export function moveWidget(layout, id, delta) {
    const i = layout.findIndex((w) => w.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= layout.length) return layout;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
}

/** Перемещение среди видимых окон (скрытые не мешают стрелкам). */
export function moveVisible(layout, id, delta) {
    const visibleIdx = layout.map((w, i) => (w.enabled ? i : -1)).filter((i) => i >= 0);
    const pos = visibleIdx.findIndex((i) => layout[i].id === id);
    const target = visibleIdx[pos + delta];
    if (pos < 0 || target === undefined) return layout;
    const next = [...layout];
    const from = visibleIdx[pos];
    [next[from], next[target]] = [next[target], next[from]];
    return next;
}

export function updateWidget(layout, id, patch) {
    return layout.map((w) => (w.id === id ? { ...w, ...patch } : w));
}

export function setWidgetOption(layout, id, key, value) {
    return layout.map((w) => (w.id === id ? { ...w, options: { ...w.options, [key]: value } } : w));
}

export function toggleInList(list, value) {
    const arr = Array.isArray(list) ? list : [];
    return arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
}

/** Перенос окна на место другого (перетаскивание мышкой). */
export function reorderLayout(layout, activeId, overId) {
    const from = layout.findIndex((w) => w.id === activeId);
    const to = layout.findIndex((w) => w.id === overId);
    if (from < 0 || to < 0 || from === to) return layout;
    const next = [...layout];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
}

/**
Виды модулей, доступные пользователю: вкладка модуля открыта его роли (как в меню) и сервер отдал данные окна
 * (при включённых правах №11 недоступные разделы приходят как forbidden). Так человек не может выбрать
 * на главном экране то, к чему у него нет доступа.
 */
export function availableTypes(role, forbiddenBlocks) {
    const forbidden = new Set(Array.isArray(forbiddenBlocks) ? forbiddenBlocks : []);
    return new Set(
        WIDGETS.filter((def) => {
            const byRights = pathAllowedByRights(def.path); // правка №58: при включённых правах — по правам
            if (byRights === false) return false;
            const roles = rolesForPath(def.path) || [];
            if (byRights === null && role && !roles.includes(role)) return false;
            return !(def.blocks || []).some((b) => forbidden.has(b));
        }).map((def) => def.id),
    );
}

export const SPAN_CLASS = { 1: "lg:col-span-1", 2: "lg:col-span-2", 3: "lg:col-span-3", 4: "lg:col-span-4" };

export const ALL_KPI_KEYS = ALL_KPI;

/** Сортировка/фильтр вакансий для окна «Вакансии». */
export function selectVacancies(items, { show = "all", sort = "severity", limit = 25 } = {}) {
    const rank = { red: 0, amber: 1, green: 2 };
    let rows = Array.isArray(items) ? [...items] : [];
    if (show === "problem") rows = rows.filter((v) => v.severity !== "green");
    if (show === "red") rows = rows.filter((v) => v.severity === "red");
    const by = {
        severity: (a, b) => rank[a.severity] - rank[b.severity] || b.days_open - a.days_open,
        days_open: (a, b) => b.days_open - a.days_open,
        active: (a, b) => b.active - a.active,
        last_move: (a, b) => (b.last_move_days ?? -1) - (a.last_move_days ?? -1),
    };
    rows.sort(by[sort] || by.severity);
    return rows.slice(0, Number(limit) || 25);
}
